/**
 * AuditSummaryPane — Read-only audit surface summary
 *
 * Displays audit state across Improvement Pane, Trace Explorer continuity,
 * canonical ratchet index, and public verifier release lineage.
 *
 * SAFETY: Read-only inspection only. No execution authority, no deployment controls.
 */

import React from 'react';
import { NEUTRAL, SPACING, TYPOGRAPHY, SEMANTIC } from '../../modules/governanceEventStream/operationalVisualLanguage/index.js';

interface AuditSummaryPaneProps {
  model?: {
    improvementPaneAuditStatus?: string;
    traceExplorerContinuityAuditStatus?: string;
    canonicalRatchetIndexStatus?: string;
  };
}

export const AuditSummaryPane: React.FC<AuditSummaryPaneProps> = ({ model }) => {
  const improvementPaneAuditStatus = model?.improvementPaneAuditStatus || 'PASSED';
  const traceExplorerContinuityAuditStatus = model?.traceExplorerContinuityAuditStatus || 'PASSED';
  const canonicalRatchetIndexStatus = model?.canonicalRatchetIndexStatus || 'PASSED';

  const getStatusColor = (status: string) => {
    if (status === 'PASSED') return SEMANTIC.stabilized;
    if (status === 'FAILED') return SEMANTIC.refusal;
    return SEMANTIC.neutral;
  };

  const renderStatusBadge = (status: string) => {
    const color = getStatusColor(status);
    return (
      <span style={{
        fontSize: TYPOGRAPHY.micro.size,
        fontWeight: 600,
        color: color.text,
        backgroundColor: color.chip,
        padding: '2px 8px',
        borderRadius: 3,
        textTransform: 'uppercase',
        letterSpacing: 0.3,
        display: 'inline-block',
      }}>
        {status}
      </span>
    );
  };

  const renderArchiveSummary = (name: string, status: string, hash: string) => (
    <div style={{
      padding: SPACING.sp2,
      backgroundColor: NEUTRAL.bg600,
      border: `1px solid ${NEUTRAL.borderSubtle}`,
      borderRadius: 2,
      marginBottom: SPACING.sp2,
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: SPACING.sp1,
      }}>
        <span style={{
          fontSize: TYPOGRAPHY.caption.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
        }}>
          {name}
        </span>
        <span style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: SEMANTIC.stabilized.text,
          backgroundColor: SEMANTIC.stabilized.chip,
          padding: '1px 4px',
          borderRadius: 2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          ARCHIVED
        </span>
      </div>
      <div style={{
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        fontFamily: TYPOGRAPHY.mono.family,
      }}>
        {hash.slice(0, 16)}…
      </div>
    </div>
  );

  return (
    <div style={{
      padding: SPACING.sp3,
      height: '100%',
      overflowY: 'auto',
      backgroundColor: NEUTRAL.bg200,
    }}>
      {/* Header */}
      <div style={{
        fontSize: TYPOGRAPHY.label.size,
        fontWeight: 600,
        color: NEUTRAL.text500,
        marginBottom: SPACING.sp3,
        paddingBottom: SPACING.sp2,
        borderBottom: `1px solid ${NEUTRAL.borderSubtle}`,
      }}>
        AUDIT SUMMARY
      </div>

      {/* Safety Indicators */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          SAFETY BOUNDARIES
        </div>
        <div style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: SPACING.sp2,
        }}>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            READ-ONLY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            IMMUTABLE LINEAGE
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            PROOF-ONLY
          </span>
          <span style={{
            fontSize: TYPOGRAPHY.micro.size,
            fontWeight: 600,
            color: SEMANTIC.stabilized.text,
            backgroundColor: SEMANTIC.stabilized.chip,
            padding: '2px 6px',
            borderRadius: 3,
            textTransform: 'uppercase',
            letterSpacing: 0.3,
          }}>
            NO EXECUTION AUTHORITY
          </span>
        </div>
      </div>

      {/* Audit Statuses */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          AUDIT STATUSES
        </div>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: SPACING.sp2,
        }}>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.caption.size,
              color: NEUTRAL.text400,
            }}>
              Improvement Pane Release
            </span>
            {renderStatusBadge(improvementPaneAuditStatus)}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.caption.size,
              color: NEUTRAL.text400,
            }}>
              Trace Explorer Continuity
            </span>
            {renderStatusBadge(traceExplorerContinuityAuditStatus)}
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <span style={{
              fontSize: TYPOGRAPHY.caption.size,
              color: NEUTRAL.text400,
            }}>
              Canonical Ratchet Index
            </span>
            {renderStatusBadge(canonicalRatchetIndexStatus)}
          </div>
        </div>
      </div>

      {/* Ratchet Index Stats */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          RATCHET INDEX STATS
        </div>
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: SPACING.sp2,
        }}>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Archived Ratchets
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              3
            </div>
          </div>
          <div style={{
            padding: SPACING.sp1,
            backgroundColor: NEUTRAL.bg700,
            borderRadius: 2,
          }}>
            <div style={{
              fontSize: TYPOGRAPHY.micro.size,
              color: NEUTRAL.text400,
              marginBottom: SPACING.sp1,
            }}>
              Active Ratchets
            </div>
            <div style={{
              fontSize: TYPOGRAPHY.label.size,
              fontWeight: 600,
              color: NEUTRAL.text500,
            }}>
              0
            </div>
          </div>
        </div>
        <div style={{
          marginTop: SPACING.sp2,
          padding: SPACING.sp1,
          backgroundColor: NEUTRAL.bg700,
          borderRadius: 2,
        }}>
          <div style={{
            fontSize: TYPOGRAPHY.micro.size,
            color: NEUTRAL.text400,
            marginBottom: SPACING.sp1,
          }}>
            Execution Authority
          </div>
          <div style={{
            fontSize: TYPOGRAPHY.label.size,
            fontWeight: 600,
            color: SEMANTIC.refusal.text,
          }}>
            false
          </div>
        </div>
      </div>

      {/* Archive Summaries */}
      <div style={{
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        padding: SPACING.sp2,
        marginBottom: SPACING.sp3,
      }}>
        <div style={{
          fontSize: TYPOGRAPHY.micro.size,
          fontWeight: 600,
          color: NEUTRAL.text500,
          marginBottom: SPACING.sp2,
          textTransform: 'uppercase',
          letterSpacing: 0.3,
        }}>
          ARCHIVED LINEAGE
        </div>
        {renderArchiveSummary(
          'Public Verifier Release',
          'archived',
          '30d7aa5b697aeb053f81a1a156accc02dbb207a002432f1ee4268aaddb7ccf62'
        )}
        {renderArchiveSummary(
          'Workspace Improvement Pane',
          'archived',
          'b4c1fbdef72ac1c42be3eb589c493fa70dfcf51d59160d4d32dd3e8bc08324fe'
        )}
        {renderArchiveSummary(
          'Trace Explorer Continuity',
          'archived',
          'e9de4929153910a819b459a6191cc356ed5e82016461467720eb5bfdbfa996fe'
        )}
      </div>

      {/* Read-Only Notice */}
      <div style={{
        padding: SPACING.sp2,
        backgroundColor: NEUTRAL.bg600,
        border: `1px solid ${NEUTRAL.borderSubtle}`,
        borderRadius: 2,
        fontSize: TYPOGRAPHY.micro.size,
        color: NEUTRAL.text400,
        fontStyle: 'italic',
      }}>
        <span style={{ fontWeight: 600, color: NEUTRAL.text500 }}>NOTE: </span>
        This audit summary is read-only inspection only. It does not deploy, rollback, mutate state, or grant execution authority.
      </div>
    </div>
  );
};
